/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AIStarsIcon,
  AdminDatePickerInputModal,
  AdminDollarInput2,
  AdminInput,
  AdminSelect,
  Button,
  Disclaimer,
  Div,
  EditPenIconRed,
  ErrorToast,
  H5,
  Icon,
  Img,
  Modal,
  P,
  PlusIcon,
  PropsModal,
  TrashIconRed,
  displayCentsInDollars,
  lightTheme,
  menuItemDescriptionMaxLength,
  menuItemNameMaxLength,
  renderProviderIcon,
  renderProviderName,
} from '@vgrubs/components';
import {
  CategoryV2Response,
  Availability,
  MenuItemV2Response,
  MenuPriceCustomizationV2Response,
  MenuV2Response,
  ModifierGroupItemV2Response,
  ModifierItemV2Response,
} from 'components/src/types/menu.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ImageInput } from '../../components/ImageInput';
import { GenerateImageModal } from '../../components/GenerateImageModal';
import { AddCustomPrice } from './AddCustomPrice';
import { DeleteCustomPriceModal } from './DeleteCustomPriceModal';
import { DeleteImageModal } from './DeleteImageModal';
import { AddModifierGroupComponent } from './ModifierGroupComponents';
import { EditModifierModal } from '../Modifiers/EditModifierModal';
import { AddNewModifierGroupModal } from '../Modifiers/AddNewModifierGroupModal';
import {
  PAUSE_CHOICE_LABELS,
  PAUSE_IN_PAST_ERROR,
  PauseChoice,
  defaultPauseUntil,
  isPauseInFuture,
  locationZoneNote,
  pauseChoiceOf,
  pauseUntilFormat,
  toPauseUntil,
  unpauseAtToWallClock,
} from './itemPause.helpers';

/** The Availability select: in stock, or one of the ways to pause. */
type AvailabilityChoice = 'available' | PauseChoice;

export const AddItemModal = ({
  addModal,
  closeModal,
  edit,
  setAddModifierGroupModal,
  menu,
  setMenu,
  modifierGroups,
  setModifierGroups,
  handleAddItem,
  handleEditItem,
  isPos,
  isBrand,
  allowedToEditMenuPrice,
  handleChangeShouldPublish,
  setLoading,
  timeZone,
}: PropsModal & {
  allowedToEditMenuPrice: boolean;
  setAddModifierGroupModal: Dispatch<SetStateAction<boolean>>;
  addModal:
    | boolean
    | {
        item: MenuItemV2Response;
        category: MenuItemV2Response | CategoryV2Response;
      }
    | null;
  menu: MenuV2Response | null;
  setMenu: Dispatch<SetStateAction<MenuV2Response | null>>;
  handleAddItem?: (...data: any[]) => void;
  handleEditItem?: (
    image: string | Blob | File | null,
    name: string,
    description: string,
    price: number,
    categoryId: string,
    modifierGroups: ModifierGroupItemV2Response[] | null,
    existingItem: MenuItemV2Response,
    customizedPrices: MenuPriceCustomizationV2Response[] | null,
    emptyForm: () => void,
    removeImage: boolean | null,
    availability: Availability,
    preparationNotes: string,
    pausedUntilEndOfTheDay: boolean,
    pauseUntil: string | null
  ) => void;
  edit?: boolean;
  modifierGroups: MenuV2Response['modifierGroups'];
  setModifierGroups: Dispatch<SetStateAction<MenuV2Response['modifierGroups']>>;
  isPos: boolean;
  isBrand: boolean;
  handleChangeShouldPublish: (shouldPublish: boolean) => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
  /** The location's - a custom pause is picked in it. */
  timeZone?: string;
}) => {
  const [image, setImage] = useState<string | Blob | File | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [category, setCategory] = useState('');
  const [price, setPrice] = useState(0);

  const [addCustomPriceModal, setAddCustomPriceModal] = useState(false);
  const [editCustomPriceModal, setEditCustomPriceModal] =
    useState<MenuPriceCustomizationV2Response | null>(null);

  const [customizedPrices, setCustomizedPrices] = useState<
    MenuPriceCustomizationV2Response[]
  >([]);

  const [deleteModal, setDeleteModal] =
    useState<MenuPriceCustomizationV2Response | null>(null);
  const [deleteImageModal, setDeleteImageModal] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const [generateImageModal, setGenerateImageModal] = useState(false);
  const [availabilityChoice, setAvailabilityChoice] =
    useState<AvailabilityChoice>('available');
  const [pauseUntil, setPauseUntil] = useState<Date[]>([]);
  const [preparationNotes, setPreparationNotes] = useState('');
  const [editModifierModal, setEditModifierModal] =
    useState<ModifierItemV2Response | null>(null);
  const [editModifierGroupModal, setEditModifierGroupModal] =
    useState<ModifierGroupItemV2Response | null>(null);
  const data = addModal;

  useEffect(() => {
    if (!menu) return;
    setModifierGroups((prev) =>
      prev.map(
        (group) => menu.modifierGroups.find((g) => g.id === group.id) || group
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu]);

  const handleValueTotalChange = useCallback((val: number) => {
    setPrice(val);
  }, []);

  const availabilityOptions = (
    <optgroup label="Select Availability">
      <option value="available">In Stock</option>
      <option value="indefinitely">{PAUSE_CHOICE_LABELS.indefinitely}</option>
      {edit && (
        <>
          <option value="endOfDay">{PAUSE_CHOICE_LABELS.endOfDay}</option>
          <option value="custom">{PAUSE_CHOICE_LABELS.custom}</option>
        </>
      )}
    </optgroup>
  );

  const zoneNote = locationZoneNote(timeZone);

  const typeOptions = (
    <optgroup label="Select Category">
      {menu?.categories?.map((r, i) => (
        <option value={r.id} key={i}>
          {r.name}
        </option>
      ))}
    </optgroup>
  );

  useEffect(() => {
    setCategory(menu?.categories?.[0]?.id.toString() || '');
    if (edit && typeof data === 'object' && data) {
      setName(data?.item?.name || '');
      setDescription(data?.item?.description || '');
      setCategory(
        data?.category?.id.toString() ||
          menu?.categories?.[0]?.id.toString() ||
          ''
      );
      setPrice(data?.item?.price || 0);
      setModifierGroups(data?.item?.modifierGroups || []);
      setImage(data?.item?.imageUrl || '');
      setCustomizedPrices(data?.item?.customizedPrices || []);
      setPreparationNotes(data?.item?.preparationNotes || '');
      // A custom pause comes back as its moment, so saving an unrelated
      // edit sends it again instead of quietly resetting it.
      const choice =
        data.item.availability === Availability.SoldOut
          ? pauseChoiceOf(data.item)
          : 'available';
      setAvailabilityChoice(choice);
      setPauseUntil(
        choice === 'custom' && data.item.unpauseAt
          ? [unpauseAtToWallClock(data.item.unpauseAt, timeZone)]
          : []
      );
    } else {
      emptyForm();
    }
  }, [data]);

  const emptyForm = () => {
    setImage('');
    setName('');
    setDescription('');
    setCategory(menu?.categories?.[0]?.id.toString() || '');
    setModifierGroups([]);
    setPrice(0);
    setCustomizedPrices([]);
    setRemoveImage(false);
    setPreparationNotes('');
    setAvailabilityChoice('available');
    setPauseUntil([]);
  };

  const availability =
    availabilityChoice === 'available'
      ? Availability.Available
      : Availability.SoldOut;
  const pausedUntilEndOfTheDay = availabilityChoice === 'endOfDay';

  const handleSubmit = () => {
    const customPause = availabilityChoice === 'custom' ? pauseUntil[0] : null;
    if (customPause && !isPauseInFuture(customPause, timeZone)) {
      return ErrorToast(PAUSE_IN_PAST_ERROR);
    }

    if (edit && typeof data === 'object') {
      handleEditItem?.(
        image,
        name,
        description,
        price,
        category,
        modifierGroups,
        data?.item as MenuItemV2Response,
        customizedPrices,
        emptyForm,
        removeImage,
        availability,
        preparationNotes,
        pausedUntilEndOfTheDay,
        customPause ? toPauseUntil(customPause) : null
      );
    } else {
      handleAddItem?.(
        image,
        name,
        description,
        price,
        category,
        modifierGroups,
        customizedPrices,
        emptyForm,
        availability,
        preparationNotes,
        pausedUntilEndOfTheDay
      );
    }
  };

  return (
    <>
      <Modal
        // Hide the item form while the Generate image (and its nested Browse
        // library) modal is open — the form state lives in this component so it
        // survives the hide and the modal re-appears when Generate closes.
        isOpen={!!addModal && !generateImageModal}
        closeModal={closeModal}
        header={edit ? 'Edit Item' : 'Add Item'}
        logo={false}
        footer={
          <Button $selected $w="100%" onClick={handleSubmit}>
            Confirm
          </Button>
        }
        $numOfFloatingButtons={1}
      >
        <Div $flex $column $center $gap="16px" $mb="32">
          <ImageInput
            image={image}
            onChange={(img) => setImage(img)}
            onDelete={() => setDeleteImageModal(true)}
          />
          {!isPos && (
            <Button
              $blueGradient
              $selected
              $w="auto"
              onClick={() => setGenerateImageModal(true)}
            >
              <Div $flex $center $gap="8px">
                <Icon>
                  <AIStarsIcon />
                </Icon>
                Generate image with AI
              </Div>
            </Button>
          )}
        </Div>
        <Div $flex $column $gap="24px">
          <AdminInput
            placeholder="Enter here"
            label="Item Name"
            value={name}
            onChange={({ target }: { target: HTMLInputElement }) =>
              setName(target.value)
            }
            maxLength={menuItemNameMaxLength}
            disabled={isPos}
            $textCenter
          />

          <AdminSelect
            value={category}
            options={typeOptions}
            onChange={({ target }: { target: HTMLSelectElement }) => {
              setCategory(target.value);
            }}
            // placeholder="Select location"
            label="Choose Category"
            disabled={isPos}
            $textCenter
          />

          <AdminDollarInput2
            label="Price"
            onValueChange={handleValueTotalChange}
            value={price}
            disabled={allowedToEditMenuPrice ? false : !isBrand && isPos}
            $textCenter
            tooltip={
              allowedToEditMenuPrice
                ? ''
                : isBrand && isPos
                ? 'Item price changes are not supported for connected POS. To apply a different price, create the same item again with the new price inside the POS portal.'
                : ''
            }
          />

          <AdminSelect
            value={availabilityChoice}
            options={availabilityOptions}
            onChange={({ target }: { target: HTMLSelectElement }) => {
              const choice = target.value as AvailabilityChoice;
              setAvailabilityChoice(choice);
              if (choice === 'custom' && !pauseUntil.length) {
                setPauseUntil([defaultPauseUntil(timeZone)]);
              }
            }}
            label="Availability"
            disabled={isPos}
            $textCenter
          />

          {availabilityChoice === 'custom' && (
            <Div>
              <AdminDatePickerInputModal
                label="Paused Until"
                format={pauseUntilFormat}
                value={pauseUntil}
                onChange={setPauseUntil}
                options={{ minDate: 'today' }}
                beforeApplyValidation={([date]) =>
                  date && isPauseInFuture(date, timeZone)
                    ? null
                    : PAUSE_IN_PAST_ERROR
                }
                $textCenter
              />
              {zoneNote && (
                <Disclaimer $m="8px 0 0" $center $color={lightTheme.darkGray}>
                  {zoneNote}
                </Disclaimer>
              )}
            </Div>
          )}

          <AdminInput
            placeholder="Enter here"
            label="Description"
            type="textarea"
            value={description}
            onChange={({ target }: { target: HTMLTextAreaElement }) =>
              setDescription(target.value)
            }
            $textareaHeight="200"
            maxLength={menuItemDescriptionMaxLength}
            disabled={!isBrand && isPos}
            $textCenter
          />

          <AdminInput
            placeholder="Enter here"
            label="Preparation Notes"
            type="textarea"
            value={preparationNotes}
            onChange={({ target }: { target: HTMLTextAreaElement }) =>
              setPreparationNotes(target.value)
            }
            $textareaHeight="200"
            disabled={isPos}
            $textCenter
          />

          {customizedPrices?.length > 0 ? (
            <div>
              <Div $flex $alignCenter $between>
                <H5 $mb="0">Custom prices for providers</H5>
                {!isPos && (
                  <Icon $pointer onClick={() => setAddCustomPriceModal(true)}>
                    <PlusIcon />
                  </Icon>
                )}
              </Div>
              <div>
                <Div
                  $flex
                  $alignCenter
                  $between
                  // $mb="8"
                  $p="12px 24px"
                >
                  <Div $flex $alignCenter $gap="12px" $width="40%">
                    <P $left $m="0">
                      Provider
                    </P>
                  </Div>
                  <Div $width="20%">
                    <P $m="0">Price</P>
                  </Div>

                  <Div $width="20%"></Div>
                </Div>

                {customizedPrices?.map(
                  ({ provider, price, pickupPrice }, i) => (
                    <Div
                      $flex
                      $alignCenter
                      $between
                      key={i}
                      $mb="8"
                      $box
                      $p="16px 24px"
                      $pointer
                      onClick={() => {
                        setEditCustomPriceModal({
                          provider,
                          price,
                          pickupPrice,
                        });
                      }}
                    >
                      <Div $flex $alignCenter $gap="12px" $width="40%">
                        <Img
                          alt=""
                          $width="32px"
                          src={renderProviderIcon(provider)}
                        />
                        <P $bold $m="0" $left>
                          {renderProviderName(provider)}
                        </P>
                      </Div>
                      <Div $width="20%">
                        <P $m="0">
                          {pickupPrice
                            ? displayCentsInDollars(pickupPrice)
                            : displayCentsInDollars(price)}
                        </P>
                      </Div>
                      <Div $width="20%" $flex $justifyEnd $gap="8px">
                        <Icon $size="32">
                          <EditPenIconRed />
                        </Icon>
                        <Icon
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteModal({
                              provider,
                              price,
                              pickupPrice,
                            });
                          }}
                        >
                          <TrashIconRed />
                        </Icon>
                      </Div>
                    </Div>
                  )
                )}
              </div>
            </div>
          ) : isPos && !allowedToEditMenuPrice ? null : (
            <Div
              $flex
              $alignCenter
              $gap="8px"
              $pointer
              onClick={() => setAddCustomPriceModal(true)}
            >
              <Icon>
                <PlusIcon />
              </Icon>

              <H5 $m="0" $color={lightTheme.primary}>
                Add Custom Prices for Providers
              </H5>
            </Div>
          )}

          {modifierGroups?.length > 0 ? (
            <>
              <Div $flex $alignCenter $between>
                <H5 $mb="0">Modifier Groups</H5>
                {!isPos && (
                  <Icon $pointer onClick={() => setAddModifierGroupModal(true)}>
                    <PlusIcon />
                  </Icon>
                )}
              </Div>
              <Div $flex $column $gap="8px">
                {modifierGroups?.map((item, i) => (
                  <AddModifierGroupComponent
                    item={item}
                    key={i}
                    edit
                    modifierGroups={modifierGroups}
                    setModifierGroups={setModifierGroups}
                    isPos={isPos}
                    handleEditModifier={(modifier) =>
                      setEditModifierModal(modifier)
                    }
                    handleEditModifierGroup={(group) =>
                      setEditModifierGroupModal(group)
                    }
                    allowedToEditMenuPrice={allowedToEditMenuPrice}
                  />
                ))}
              </Div>
            </>
          ) : (
            !isPos && (
              <Div
                $flex
                $alignCenter
                $gap="8px"
                $pointer
                onClick={() => setAddModifierGroupModal(true)}
              >
                <Icon>
                  <PlusIcon />
                </Icon>

                <H5 $m="0" $color={lightTheme.primary}>
                  Add Modifier Groups
                </H5>
              </Div>
            )
          )}
        </Div>
      </Modal>

      <AddCustomPrice
        customPriceModal={addCustomPriceModal}
        customizedPrices={customizedPrices}
        setCustomizedPrices={setCustomizedPrices}
        closeModal={() => {
          setAddCustomPriceModal(false);
        }}
      />

      <AddCustomPrice
        customPriceModal={editCustomPriceModal}
        customizedPrices={customizedPrices}
        setCustomizedPrices={setCustomizedPrices}
        closeModal={() => {
          setEditCustomPriceModal(null);
        }}
        edit
      />

      <DeleteCustomPriceModal
        deleteModal={deleteModal}
        closeModal={() => {
          setDeleteModal(null);
        }}
        setCustomizedPrices={setCustomizedPrices}
      />

      <DeleteImageModal
        deleteModal={deleteImageModal}
        closeModal={() => {
          setDeleteImageModal(false);
        }}
        setImage={setImage}
        setRemoveImage={setRemoveImage}
      />

      <GenerateImageModal
        isOpen={generateImageModal}
        closeModal={() => setGenerateImageModal(false)}
        itemName={name}
        itemDescription={description}
        onAccept={(blob) => {
          setImage(blob);
          setRemoveImage(false);
        }}
      />

      <EditModifierModal
        editModifierModal={editModifierModal}
        closeModal={() => {
          setEditModifierModal(null);
        }}
        setMenu={setMenu}
        handleChangeShouldPublish={handleChangeShouldPublish}
        setLoading={setLoading}
      />

      <AddNewModifierGroupModal
        addNewModal={editModifierGroupModal}
        closeModal={() => {
          setEditModifierGroupModal(null);
        }}
        setMenu={setMenu}
        setLoading={setLoading}
        edit
        handleChangeShouldPublish={handleChangeShouldPublish}
      />
    </>
  );
};
